import React from "react";

/**
 * Modal component to display resume-job match analysis results
 */
function MatchAnalysisModal({ analysis, jobTitle, onClose, isLoading, error }) {
  if (isLoading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "3rem",
            maxWidth: "400px",
            textAlign: "center",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              border: "4px solid #e9ecef",
              borderTopColor: "#007bff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 1.5rem",
            }}
          />
          <style>
            {`@keyframes spin { to { transform: rotate(360deg); } }`}
          </style>
          <h3 style={{ margin: "0 0 0.5rem", color: "#2c3e50" }}>
            Analyzing Your Match
          </h3>
          <p style={{ margin: 0, color: "#7f8c8d", fontSize: "0.9rem" }}>
            Comparing your resume against the job requirements...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "2rem",
            maxWidth: "450px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>❌</div>
            <h3 style={{ margin: "0 0 0.5rem", color: "#dc3545" }}>
              Analysis Failed
            </h3>
            <p style={{ margin: 0, color: "#7f8c8d", fontSize: "0.9rem" }}>
              {error}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "100%",
              padding: "0.75rem",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  const getMatchColor = (rate) => {
    if (rate >= 75) return "#28a745";
    if (rate >= 60) return "#ffc107";
    if (rate >= 40) return "#fd7e14";
    return "#dc3545";
  };

  const getMatchLabel = (rate) => {
    if (rate >= 90) return "Excellent Match!";
    if (rate >= 75) return "Strong Match";
    if (rate >= 60) return "Good Match";
    if (rate >= 40) return "Moderate Match";
    return "Needs Improvement";
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "1rem",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          maxWidth: "600px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "1.5rem 2rem",
            borderBottom: "1px solid #e9ecef",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={{ margin: "0 0 0.25rem", color: "#2c3e50", fontSize: "1.3rem" }}>
              Match Analysis
            </h2>
            <p style={{ margin: 0, color: "#7f8c8d", fontSize: "0.85rem" }}>
              {jobTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: "#7f8c8d",
              padding: "0.25rem",
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "2rem", overflowY: "auto", flex: 1 }}>
          {/* Match Rate Circle */}
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <div
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                border: `8px solid ${getMatchColor(analysis.matchRate)}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem",
                backgroundColor: "#f8f9fa",
              }}
            >
              <span
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "700",
                  color: getMatchColor(analysis.matchRate),
                  lineHeight: 1,
                }}
              >
                {analysis.matchRate}%
              </span>
            </div>
            <div
              style={{
                color: getMatchColor(analysis.matchRate),
                fontWeight: "600",
                fontSize: "1.1rem",
                marginBottom: "0.5rem",
              }}
            >
              {getMatchLabel(analysis.matchRate)}
            </div>
            <p style={{ color: "#7f8c8d", fontSize: "0.9rem", margin: 0 }}>
              {analysis.summary}
            </p>
          </div>

          {/* Matching Skills */}
          {analysis.matchingSkills && analysis.matchingSkills.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <h4 style={{ color: "#28a745", margin: "0 0 0.75rem", fontSize: "1rem" }}>
                ✅ Matching Skills ({analysis.matchingSkills.length})
              </h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {analysis.matchingSkills.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: "#d4edda",
                      color: "#155724",
                      padding: "0.4rem 0.75rem",
                      borderRadius: "20px",
                      fontSize: "0.85rem",
                      fontWeight: "500",
                    }}
                    title={item.evidence}
                  >
                    {item.skill}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Missing Requirements */}
          {analysis.missingRequirements && analysis.missingRequirements.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <h4 style={{ color: "#dc3545", margin: "0 0 0.75rem", fontSize: "1rem" }}>
                ⚠️ Gaps to Address ({analysis.missingRequirements.length})
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {analysis.missingRequirements.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeeba",
                      padding: "0.75rem",
                      borderRadius: "6px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.25rem" }}>
                      <span style={{ fontWeight: "600", color: "#856404" }}>
                        {item.requirement}
                      </span>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          padding: "0.15rem 0.5rem",
                          borderRadius: "10px",
                          backgroundColor: item.importance === "critical" ? "#f8d7da" : item.importance === "preferred" ? "#fff3cd" : "#e2e3e5",
                          color: item.importance === "critical" ? "#721c24" : item.importance === "preferred" ? "#856404" : "#383d41",
                        }}
                      >
                        {item.importance}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: "0.8rem", color: "#666" }}>
                      💡 {item.suggestion}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strengths */}
          {analysis.strengths && analysis.strengths.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <h4 style={{ color: "#007bff", margin: "0 0 0.75rem", fontSize: "1rem" }}>
                💪 Your Strengths
              </h4>
              <ul style={{ margin: 0, paddingLeft: "1.25rem", color: "#2c3e50" }}>
                {analysis.strengths.map((strength, index) => (
                  <li key={index} style={{ marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {analysis.recommendations && analysis.recommendations.length > 0 && (
            <div>
              <h4 style={{ color: "#6f42c1", margin: "0 0 0.75rem", fontSize: "1rem" }}>
                📝 Recommendations
              </h4>
              <ul style={{ margin: 0, paddingLeft: "1.25rem", color: "#2c3e50" }}>
                {analysis.recommendations.map((rec, index) => (
                  <li key={index} style={{ marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "1rem 2rem",
            borderTop: "1px solid #e9ecef",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default MatchAnalysisModal;

